import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Tooltip,
  Button,
  ButtonGroup
} from '@mui/material';
import { TextFields, Image, FormatAlignLeft, FormatAlignCenter, FormatAlignRight, FormatAlignJustify } from '@mui/icons-material';
import { getPresetNames, getPreset, getFramePresetNames, getFramePreset } from '../textStyle';

const BlogPresetSelector = ({ onStyleSelect, onFrameSelect, onAlignmentSelect, currentStyle = null, currentFrame = null, currentAlignment = 'center', type = 'text' }) => {
  const handleStyleSelect = (presetName) => {
    const preset = getPreset(presetName);
    console.log('🎨 BlogPresetSelector: Applying preset:', presetName, preset);
    onStyleSelect(presetName, preset);
  };

  const handleFrameSelect = (presetName) => {
    const preset = getFramePreset(presetName);
    console.log('🖼️ BlogPresetSelector: Applying frame preset:', presetName, preset);
    console.log('🖼️ onFrameSelect function:', onFrameSelect);
    onFrameSelect(presetName, preset);
  };

  const handleAlignmentSelect = (alignment) => {
    console.log('📝 BlogPresetSelector: Applying text alignment:', alignment);
    onAlignmentSelect(alignment);
  };

  if (type === 'text' || type === 'title') {
    const presetNames = getPresetNames();
    
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <TextFields fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Font Presets
          </Typography>
        </Box>
        
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Click any style to quickly apply a complete typography set.
        </Typography>

        <Grid container spacing={1}>
          {presetNames.map((presetName) => {
            const preset = getPreset(presetName);
            const isSelected = currentStyle === presetName;
            
            return (
              <Grid item key={presetName}>
                <Tooltip 
                  title={`${presetName} - ${preset.fontSize} ${preset.fontWeight}`}
                  placement="top"
                >
                  <Paper
                    elevation={isSelected ? 3 : 1}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: isSelected ? '#e3f2fd' : '#ffffff',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: isSelected ? '#e3f2fd' : '#f5f5f5',
                        borderColor: '#1976d2',
                        transform: 'translateY(-1px)'
                      },
                      minWidth: 80,
                      maxWidth: 120
                    }}
                    onClick={() => handleStyleSelect(presetName)}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        ...preset,
                        display: 'block',
                        textAlign: 'center',
                        wordBreak: 'break-word',
                        lineHeight: 1.2,
                        fontSize: '10px'
                      }}
                    >
                      {presetName.charAt(0).toUpperCase() + presetName.slice(1)}
                    </Typography>
                  </Paper>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>

        {/* Text Alignment Options */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Text Alignment
          </Typography>
          
          <ButtonGroup size="small" variant="outlined" sx={{ mb: 1 }}>
            <Tooltip title="Align Left" placement="top">
              <Button
                onClick={() => handleAlignmentSelect('left')}
                sx={{ 
                  px: 1,
                  py: 0.5,
                  backgroundColor: currentAlignment === 'left' ? '#e3f2fd' : 'transparent',
                  borderColor: currentAlignment === 'left' ? '#1976d2' : '#e0e0e0',
                  color: currentAlignment === 'left' ? '#1976d2' : 'inherit',
                  '&:hover': {
                    backgroundColor: currentAlignment === 'left' ? '#e3f2fd' : '#f5f5f5',
                    borderColor: '#1976d2'
                  }
                }}
              >
                <FormatAlignLeft fontSize="small" />
              </Button>
            </Tooltip>
            
            <Tooltip title="Align Center" placement="top">
              <Button
                onClick={() => handleAlignmentSelect('center')}
                sx={{ 
                  px: 1,
                  py: 0.5,
                  backgroundColor: currentAlignment === 'center' ? '#e3f2fd' : 'transparent',
                  borderColor: currentAlignment === 'center' ? '#1976d2' : '#e0e0e0',
                  color: currentAlignment === 'center' ? '#1976d2' : 'inherit',
                  '&:hover': {
                    backgroundColor: currentAlignment === 'center' ? '#e3f2fd' : '#f5f5f5',
                    borderColor: '#1976d2'
                  }
                }}
              >
                <FormatAlignCenter fontSize="small" />
              </Button>
            </Tooltip>
            
            <Tooltip title="Align Right" placement="top">
              <Button
                onClick={() => handleAlignmentSelect('right')}
                sx={{ 
                  px: 1,
                  py: 0.5,
                  backgroundColor: currentAlignment === 'right' ? '#e3f2fd' : 'transparent',
                  borderColor: currentAlignment === 'right' ? '#1976d2' : '#e0e0e0',
                  color: currentAlignment === 'right' ? '#1976d2' : 'inherit',
                  '&:hover': {
                    backgroundColor: currentAlignment === 'right' ? '#e3f2fd' : '#f5f5f5',
                    borderColor: '#1976d2'
                  }
                }}
              >
                <FormatAlignRight fontSize="small" />
              </Button>
            </Tooltip>
            
            <Tooltip title="Justify Text" placement="top">
              <Button
                onClick={() => handleAlignmentSelect('justify')}
                sx={{ 
                  px: 1,
                  py: 0.5,
                  backgroundColor: currentAlignment === 'justify' ? '#e3f2fd' : 'transparent',
                  borderColor: currentAlignment === 'justify' ? '#1976d2' : '#e0e0e0',
                  color: currentAlignment === 'justify' ? '#1976d2' : 'inherit',
                  '&:hover': {
                    backgroundColor: currentAlignment === 'justify' ? '#e3f2fd' : '#f5f5f5',
                    borderColor: '#1976d2'
                  }
                }}
              >
                <FormatAlignJustify fontSize="small" />
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Box>
      </Box>
    );
  }

  if (type === 'media') {
    const framePresetNames = getFramePresetNames();
    
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Image fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Frame Presets
          </Typography>
        </Box>
        
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Choose a frame style for your media.
        </Typography>

        <ButtonGroup size="small" variant="outlined" sx={{ mb: 1 }}>
          {framePresetNames.map((presetName) => {
            const isSelected = currentFrame === presetName;
            
            return (
              <Button
                key={presetName}
                onClick={() => handleFrameSelect(presetName)}
                sx={{ 
                  fontSize: '0.75rem',
                  px: 1,
                  py: 0.5,
                  backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                  borderColor: isSelected ? '#1976d2' : '#e0e0e0',
                  color: isSelected ? '#1976d2' : 'inherit',
                  '&:hover': {
                    backgroundColor: isSelected ? '#e3f2fd' : '#f5f5f5',
                    borderColor: '#1976d2'
                  }
                }}
                title={`${presetName} frame style`}
              >
                {presetName.charAt(0).toUpperCase() + presetName.slice(1)}
              </Button>
            );
          })}
        </ButtonGroup>
      </Box>
    );
  }

  return null;
};

export default BlogPresetSelector;
